import { State, AuthTypeInfo, FocusedFieldState } from './types';
import {
  onValidateTargetSystem,
  onValidateTLS,
  onValidateApiKey,
  onValidateOauth,
  onValidateBasicOrDigest,
  onValidateHMACSHA256,
} from './validations';
import { Action } from '../../../../types';
import { MedisanteTargetSystem } from '../../types';
import { AuthUser } from '../../../../contexts/AuthProvider/types';
import { IManageContextState } from '../../../../contexts/ManageContext/ManageContext';

export const onSaveApiKey = (state: State) => {
  const {
    name,
    location,
    format,
    selectedAuthType,
    key,
    updateKey,
    isDefault,
    allowIP,
  } = state;

  var ts: any = {
    name,
    authType: selectedAuthType!.type,
    format: format,
    url: location,
    isDefault: isDefault,
    allowIP,
  };

  if (updateKey === true) {
    ts.secret = key;
  }
  return ts;
};

export const onSaveOauth = (state: State) => {
  const {
    name,
    location,
    format,
    selectedAuthType,
    updateKey,
    password,
    authEndpoint,
    grantType,
    username,
    code,
    redirectURI,
    privateKey,
    privateKeyPassword,
    isDefault,
    allowIP,
  } = state;

  var ts: any = {
    name,
    authType: selectedAuthType!.type,
    format: format,
    url: location,
    isDefault: isDefault,
    allowIP,
  };

  ts.authData = {
    authEndpoint: authEndpoint,
    grantType: grantType,
    username: username,
  };

  if (code) {
    ts.authData.code = code;
  }

  if (redirectURI) {
    ts.authData.redirectURI = redirectURI;
  }

  if (updateKey === true) {
    ts.secret = password;
    if (privateKey) {
      ts.privateKey = privateKey;
    }
    if (privateKeyPassword) {
      ts.privateKeyPassword = privateKeyPassword;
    }
  }

  return ts;
};

export const onSaveBasicOrDigest = (state: State) => {
  const {
    name,
    location,
    format,
    selectedAuthType,
    updateKey,
    password,
    username,
    isDefault,
    allowIP,
  } = state;

  var ts: any = {
    name,
    authType: selectedAuthType!.type,
    format: format,
    url: location,
    username: username,
    isDefault: isDefault,
    allowIP,
  };

  if (updateKey === true) {
    ts.secret = password;
  }
  return ts;
};

export const onSaveTLS = (state: State) => {
  const { allowIP, name, location, format, selectedAuthType, isDefault } =
    state;
  var ts: any = {
    name,
    authType: selectedAuthType!.type,
    format: format,
    url: location,
    isDefault: isDefault,
    allowIP,
  };
  return ts;
};

export const onSaveHMACSHA256 = (state: State) => {
  const { allowIP, name, location, format, selectedAuthType, isDefault } =
    state;

  var ts: any = {
    name,
    authType: selectedAuthType!.type,
    format: format,
    url: location,
    isDefault: isDefault,
    allowIP,
  };
  return ts;
};

export const apiFormats = [
  {
    key: 'json/v1',
    value: 'json/v1',
    description: 'Medisanté Standard (JSON)',
  },
  { key: 'xml/v1', value: 'xml/v1', description: 'Medisanté Standard (XML)' },
  { key: 'fhir4json', value: 'fhir4/json/v1', description: 'FHIRv4 (JSON)' },
  { key: 'fhir4xml', value: 'fhir4/xml/v1', description: 'FHIRv4 (XML)' },
];

export const HL7Formats = ['fhir4/json/v1', 'fhir4/xml/v1'];
export const UnsupportedHL7Devices = [
  'IR42',
  'PO200',
  'FORA6',
  'IR20',
  'AOJ-25A',
];
export const UnsupportedHL7Types = [
  'ECG (raw)',
  'Ketones',
  'Cholesterol',
  'Uric Acid',
  'Temperature',
  'SpO2',
];

export const authTypes: { [ID: string]: AuthTypeInfo } = {
  apikey: {
    type: 'apikey',
    description: 'API Key',
    onSave: onSaveApiKey,
    onValidate: onValidateApiKey,
  },
  oauth: {
    type: 'oauth',
    description: 'OAuth 2.0',
    onSave: onSaveOauth,
    onValidate: onValidateOauth,
  },
  basic: {
    type: 'basic',
    description: 'Basic',
    onSave: onSaveBasicOrDigest,
    onValidate: onValidateBasicOrDigest,
  },
  digest: {
    type: 'digest',
    description: 'Digest authentication',
    onSave: onSaveBasicOrDigest,
    onValidate: onValidateBasicOrDigest,
  },
  tls: {
    type: 'tls',
    description: 'TLS',
    onSave: onSaveTLS,
    onValidate: onValidateTLS,
  },
  hmacsha256: {
    type: 'hmacsha256',
    description: 'HMAC SHA256',
    onSave: onSaveHMACSHA256,
    onValidate: onValidateHMACSHA256,
  },
};

export const orderedAuthTypes = [
  authTypes['apikey'],
  authTypes['oauth'],
  authTypes['basic'],
  authTypes['digest'],
  authTypes['hmacsha256'],
];

export const grantTypes = [
  {
    key: 'client_credentials',
    value: 'client_credentials',
    description: 'Client credentials',
  },
  {
    key: 'authorization_code',
    value: 'authorization_code',
    description: 'Authorization code',
  },
  {
    key: 'jwt_bearer',
    value: 'jwt_bearer',
    description: 'JWT bearer',
  },
];

export const initialState: State = {
  name: '',
  organization: [],
  location: '',
  key: '',
  format: '',
  selectedAuthType: null,
  authEndpoint: '',
  grantType: '',
  username: '',
  password: '',
  isCareSelected: false,
  updateKey: true,
  forceChange: true,
  messages: false,
  validation: {
    name: true,
    location: true,
    selectedAuthType: true,
    format: true,
    key: true,
    authEndpoint: true,
    grantType: true,
    username: true,
    password: true,
    code: true,
    organization: true,
    redirectURI: true,
    privateKey: true,
    privateKeyPassword: true,
    allowIP: true,
  },
  valid: false,
  legacy: '',
  code: '',
  redirectURI: '',
  privateKey: '',
  privateKeyPassword: '',
  isDefault: false,
  allowIP: '',
};

export const editTargetSystemReducer = (state: State, action: Action) => {
  const { data } = action;

  const format = data && data.format ? data.format : '';
  const validFormat = apiFormats.some((item) => item.value === format);
  if (!validFormat) {
    state.legacy = format;
  } else {
    state.legacy = '';
  }

  switch (action.type) {
    case 'auth-type-changed':
      return { ...state, selectedAuthType: action.data };
    case 'model-changed': {
      let newState = {
        ...state,
        name: data.name,
        location: data.url,
        format: data.format,
        username: data.username,
        selectedAuthType: authTypes[data.authType],
        allowIP: data.allowIP,
      };
      const onValidate = newState.selectedAuthType
        ? newState.selectedAuthType.onValidate
        : onValidateTargetSystem;

      if (data.authType) {
        newState.selectedAuthType = authTypes[data.authType];
      }

      if (data.authData) {
        newState.authEndpoint = data.authData.authEndpoint;
        newState.grantType = data.authData.grantType;
        newState.username = data.authData.username;
        newState.code = data.authData.code;
        newState.redirectURI = data.authData.redirectURI;
      }

      newState.updateKey = false;
      newState.forceChange = false;
      newState.isDefault = data.isDefault;

      onValidate(newState);

      return newState;
    }
    case 'value-changed': {
      let newState = { ...state, ...data };
      const onValidate = newState.selectedAuthType
        ? newState.selectedAuthType.onValidate
        : onValidateTargetSystem;

      onValidate(newState);

      return newState;
    }
    case 'reset': {
      return initialState;
    }
  }

  return state;
};

export const stripHttpsFromTheBegining = (text: string) => {
  return text.replace(/https:\/\//g, '');
};

export const appendHttpsOnTheBegining = (text: string) => {
  return !text.startsWith('https://') ? 'https://' + text : text;
};

export const focusedFieldReducer = (
  state: FocusedFieldState,
  action: Action
): FocusedFieldState => {
  const key = action.type as keyof FocusedFieldState;
  return {
    [key]: true,
  };
};

export const isTargetSystemFromDemoOrg = (
  targetSystem: MedisanteTargetSystem,
  authUser: AuthUser
) => {
  const org = authUser.orgs.find((o) => o.id === targetSystem.owner);
  return !!org && org.demo;
};

export const isCareVisible = (
  careCount: number | null,
  mState: IManageContextState,
  organization: string | null,
  targetSystems?: MedisanteTargetSystem[]
) => {
  const careTS = targetSystems?.filter((t) => t.name === 'Medisanté Care');
  if (
    (!!careCount && careCount >= 1) ||
    !mState.selectedOrgs ||
    mState.selectedOrgs.length === 0 ||
    (mState.selectedOrgs.length > 1 && !organization) ||
    (careTS && careTS.length > 0)
  ) {
    return false;
  }
  return mState.selectedOrgs.length === 1
    ? mState.selectedOrgs.every((o) => o.canCreateCare)
    : mState.selectedOrgs
        .filter((o) => o.id === organization)
        .every((o) => o.canCreateCare);
};

export const isPrivateKeyEncrypted = (privateKey: string) => {
  const expression =
    /^(?:-----BEGIN ([^-]+)-----)\n*(?:Proc-Type: .+)\n*(?:DEK-Info: .+)\n*([a-z0-9+/=\n]+)\n*(?:-----END \1-----)$/im;
  const regex = new RegExp(expression);
  return regex.test(privateKey);
};

export const nameOptions = [
  'Development',
  'Production',
  'Test',
  'Staging',
  'Live',
  'Default',
  'Custom',
];
export const nameOptionCustom = 'Custom';
